import { createContext, ReactElement, useContext, useState } from 'react'
import { Helmet } from 'react-helmet'
import { useSelector } from 'react-redux'
import { useLocation } from 'react-router-dom'
import { FeatureDict, selectFeatureIncludes } from '@authed/selectors'
import { getSiteMap } from './config'
import { CustomTitle } from './types'
import { getNestedTitleFromPath } from './utils'

interface Value {
  customTitle: CustomTitle
  addPrefix: (prefix: string) => void
  removePrefix: () => void
  overridePageTitle: (title: string) => void
}

export const PageTitleContext = createContext<Value | null>(null)

interface Props {
  children: ReactElement | ReactElement[]
}

export const PageTitleContextProvider = ({ children }: Props) => {
  const { pathname } = useLocation()
  const [prefix, setPrefix] = useState<string | null>(null)
  const [customTitle, setCustomTitle] = useState<CustomTitle>(null)
  const [DATASETS_PAGE_ENABLED = false] = useSelector(
    selectFeatureIncludes([FeatureDict.DATASETS_PAGE]),
  )

  const pathKeys: string[] = pathname.split('/').filter(Boolean)
  const pathHasId = Boolean(pathKeys.find(path => !isNaN(+path)))
  const pathKeysIdsRemoved: string[] = pathKeys.filter(key => isNaN(+key))

  const siteMap = getSiteMap(DATASETS_PAGE_ENABLED)

  const title = getNestedTitleFromPath({
    keys: pathKeysIdsRemoved,
    nestedPaths: siteMap,
    prefix,
  })

  const value = {
    customTitle,
    addPrefix,
    removePrefix,
    overridePageTitle,
  }

  return (
    <PageTitleContext.Provider value={value}>
      <>
        <Helmet>
          {window.PEN_TEST_ID && (
            <meta name="insight-app-sec-validation" content={window.PEN_TEST_ID} />
          )}
          <title>{customTitle || title}</title>
        </Helmet>
        {children}
      </>
    </PageTitleContext.Provider>
  )

  function addPrefix(newPrefix: string): void {
    if (!pathHasId) return
    setPrefix(newPrefix)
  }

  function removePrefix(): void {
    setPrefix(null)
  }

  function overridePageTitle(newPageTitle: string): void {
    setCustomTitle(newPageTitle)
  }
}

export const usePageTitleContext = () => {
  const context = useContext(PageTitleContext)

  if (!context) {
    throw new Error('usePageTitleContext must be used within a PageTitleContextProvider')
  }

  return useContext(PageTitleContext)!
}
